import React from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { LayoutDashboard, Clock, ListChecks, CalendarDays, LayoutGrid, LogOut } from "lucide-react";
import { useEmployeeAuth } from "../../context/EmployeeAuthContext";
import EmpChangePin from "./EmpChangePin";
import EmpNotificationBell from "./EmpNotificationBell";
import { roleLabel } from "./empRoles";

// 5-slot bottom navigation. Everything else lives in the HRD Hub ("Lainnya").
const TABS = [
  { to: "/karyawan", end: true, label: "Beranda", icon: LayoutDashboard, testid: "emp-nav-dashboard" },
  { to: "/karyawan/absensi", label: "Absensi", icon: Clock, testid: "emp-nav-attendance" },
  { to: "/karyawan/checklist", label: "Tugas", icon: ListChecks, testid: "emp-nav-tasks" },
  { to: "/karyawan/jadwal", label: "Jadwal", icon: CalendarDays, testid: "emp-nav-schedule" },
  { to: "/karyawan/menu", label: "Lainnya", icon: LayoutGrid, testid: "emp-nav-more" },
];

export default function EmployeeLayout() {
  const { employee, logout } = useEmployeeAuth();
  const navigate = useNavigate();
  if (!employee) return <Navigate to="/karyawan/login" replace />;
  // Forced first-login / post-reset PIN change blocks the whole portal until done.
  if (employee.pin_must_change) return <EmpChangePin />;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto flex min-h-screen max-w-md flex-col bg-gradient-to-b from-slate-950 to-slate-900">
        {/* Header */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-800 bg-slate-950/80 px-4 py-3 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 font-bold text-slate-900">
              {(employee.nickname || employee.full_name || "?").charAt(0)}
            </div>
            <div className="leading-tight">
              <p className="text-sm font-bold" data-testid="emp-header-name">{employee.nickname || employee.full_name}</p>
              <p className="text-[11px] text-slate-400">{roleLabel(employee.position)} · {employee.branch_name || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-[10px] font-semibold uppercase tracking-wide text-amber-400 sm:inline" data-testid="emp-portal-brand">Portal HRD</span>
            <EmpNotificationBell />
            <button onClick={logout} data-testid="emp-logout" className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto px-4 pb-24 pt-4">
          <Outlet />
        </main>

        {/* Bottom nav */}
        <nav className="fixed bottom-0 left-1/2 z-20 flex w-full max-w-md -translate-x-1/2 border-t border-slate-800 bg-slate-950/95 backdrop-blur">
          {TABS.map((tabb) => (
            <NavLink key={tabb.to} to={tabb.to} end={tabb.end} data-testid={tabb.testid}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold transition-colors ${isActive ? "text-amber-400" : "text-slate-500"}`}>
              <tabb.icon size={20} />
              {tabb.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
